/**
 * StreakBadge - Compact streak indicator
 * Phase 13.6 - Form Streaks & Trends
 */
import React from 'react'
import type { StreakInfo } from '../types'

interface StreakBadgeProps {
  streak: StreakInfo
  showLabel?: boolean
  compact?: boolean
}

const STREAK_CONFIG = {
  W: { label: 'Win', color: 'var(--green)', bgColor: 'rgba(34, 197, 94, 0.15)' },
  D: { label: 'Draw', color: 'var(--amber)', bgColor: 'rgba(245, 158, 11, 0.15)' },
  L: { label: 'Loss', color: 'var(--red)', bgColor: 'rgba(239, 68, 68, 0.15)' },
} as const

export function StreakBadge({ streak, showLabel = false, compact = false }: StreakBadgeProps) {
  if (!streak.type || streak.count === 0) {
    return null
  }

  const config = STREAK_CONFIG[streak.type as keyof typeof STREAK_CONFIG]
  if (!config) return null

  const label = showLabel
    ? `${streak.count} ${config.label}${streak.count > 1 ? 's' : ''}`
    : `${streak.type}${streak.count}`

  return (
    <span
      className={`streak-badge ${compact ? 'compact' : ''}`}
      style={{
        color: config.color,
        backgroundColor: config.bgColor,
      }}
      title={`${streak.count} ${config.label} streak`}
    >
      {label}
    </span>
  )
}

interface UnbeatenBadgeProps {
  count: number
  minToShow?: number
}

export function UnbeatenBadge({ count, minToShow = 3 }: UnbeatenBadgeProps) {
  if (count < minToShow) return null

  return (
    <span
      className="streak-badge unbeaten"
      title={`${count} matches unbeaten`}
    >
      {count} Unbeaten
    </span>
  )
}

export function WinlessBadge({ count, minToShow = 3 }: UnbeatenBadgeProps) {
  if (count < minToShow) return null

  return (
    <span
      className="streak-badge winless"
      title={`${count} matches without a win`}
    >
      {count} Winless
    </span>
  )
}

interface FormStringProps {
  form: string
  maxLength?: number
}

export function FormString({ form, maxLength = 5 }: FormStringProps) {
  if (!form) return null

  const displayForm = form.slice(-maxLength).toUpperCase()

  return (
    <span className="form-string">
      {displayForm.split('').map((char, i) => (
        <span
          key={i}
          className={`form-char ${char === 'W' ? 'win' : char === 'D' ? 'draw' : 'loss'}`}
        >
          {char}
        </span>
      ))}
    </span>
  )
}
